import { SITE_RECAPTCHA_KEY } from '../../environments/environment';
import {
	ConsentTaxFolderUI,
	ResponseConsentTaxFolder,
} from '../interfaces/consent-tax-folder.interface';
import {
	CustomerDataUI,
	RequestValidateCustomerData,
	ResponseValidateCustomerData,
	ValidateCustomerDataUI,
} from '../interfaces/customer-data.interface';
import {
	GenerateSendOtpUI,
	ResponseGenerateSendOtp,
} from '../interfaces/generate-send-otp.interface';
import { PurposesResponse, PurposesUI } from '../interfaces/get-purposes';
import {
	InitialParametersModel,
	ResponseInitialParameters,
} from '../interfaces/initial-parameters.interface';
import { OtpValidationUI, ResponseOtpValidation } from '../interfaces/otp-validation.interface';
import { RegisterPurposesRequest } from '../interfaces/register-purposes';
import {
	RequestTaxFolder,
	ResponseTaxFolder,
	ResponseTaxFolderUI,
} from '../interfaces/tax-folder.interface';
import { CTE_APLICA, SIMULATOR_BY_TYPE, VALID_PASSWORD } from './constants';
import { formatRutWithDash } from './utils';

export const mapper: {
	initialParameters: (resp: ResponseInitialParameters) => InitialParametersModel;
	validateData: (resp: ResponseValidateCustomerData) => ValidateCustomerDataUI;
	requestValidateData: (form: CustomerDataUI, recaptcha: string) => RequestValidateCustomerData;
	dataConsent: (resp: ResponseConsentTaxFolder) => ConsentTaxFolderUI;
	requestTaxFolder: (encrypted: string, rutEmpresa: string, consentId: string) => RequestTaxFolder;
	taxFolder: (resp: ResponseTaxFolder) => ResponseTaxFolderUI;
	otpGenerated: (resp: ResponseGenerateSendOtp) => GenerateSendOtpUI;
	otpValidation: (resp: ResponseOtpValidation) => OtpValidationUI;
	purposes: (resp: PurposesResponse) => PurposesUI;
	requestRegisterPurposes: (
		rut: string,
		idForm: string,
		accepted: string
	) => RegisterPurposesRequest;
} = {
	initialParameters: resp => ({
		title: resp.titulo,
		subtitle: resp.subTitulo,
		content: resp.contenido,
		surveyBanner: resp.encuesta,
		nameLeakSurvey: resp.encuestaFuga,
		idFeedbackSurvey: resp.idEncuestaFeedback,
		idLeakSurvey: resp.idEncuestaFuga,
		simulatorType: resp.tipoSimulador,
		simulatorUrl: SIMULATOR_BY_TYPE[resp.simulador as keyof typeof SIMULATOR_BY_TYPE] ?? null,
		salesLevels: resp.nivelVentas.map((item: { rango: string; nivel: string }) => ({
			range: item.rango,
			level: item.nivel,
		})),
	}),
	validateData: resp => ({
		uuid: resp.uuid,
		id: resp.id,
		approvedRisk: resp.aprobadoRiesgo,
		applyCte: resp.cte === CTE_APLICA,
	}),
	requestValidateData: (form, recaptcha) => ({
		captchaUserVerify: recaptcha,
		publicKey: SITE_RECAPTCHA_KEY,
		rutCliente: form.companyRut,
		rutUsuario: form.userRut,
		correo: form.email,
		telefono: form.phoneNumber,
		nivelVenta: form.salesLevel,
	}),
	dataConsent: resp => ({
		id: resp.consentimientoId,
		expiration: resp.expiracion,
		title: resp.consentimientoDatos[0].titulo,
		detail: resp.consentimientoDatos[0].informacion[0].detalle,
		authorization: resp.autorizaciones[0].texto,
	}),
	requestTaxFolder: (encrypted, rutEmpresa, consentId) => ({
		rut: rutEmpresa,
		consentimientoId: consentId,
		claveCifrada: encrypted,
	}),
	taxFolder: resp => ({
		validPassword: resp.estado === VALID_PASSWORD,
	}),
	otpGenerated: resp => ({
		secondsExpirationOtp: resp.vencimientoOtpSeg,
		otpGenerationTime: resp.horaGeneracionOtp,
		hiddenTarget: resp.destinoOfuscado,
	}),
	otpValidation: resp => ({
		otpValid: resp.valido,
		attempts: resp.intentosRestantes,
	}),
	purposes: resp => ({
		idForm: resp.idForm,
		purposes: resp.purposes.map(purpose => ({
			id: purpose.id,
			description: purpose.description,
			mandatory: purpose.mandatory,
		})),
	}),
	requestRegisterPurposes: (rut, idForm, accepted) => ({
		identifier: formatRutWithDash(rut),
		idFormulario: idForm,
		purposes: Object.entries(accepted)
			.filter(([, accepted]) => accepted)
			.map(([id]) => ({ id, transactionType: 'CONFIRMED' })),
	}),
};
